import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { imgSrc, gbp } from '../lib/shop';

export default function CartPage() {
  const { items, updateQuantity, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-16 h-16 text-pink-300 mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Your cart is empty</h1>
        <p className="text-gray-600 mb-8">Browse our cleaning products and gift vouchers.</p>
        <Button asChild className="bg-pink-500 hover:bg-pink-600 text-white rounded-full"><Link to="/shop" data-testid="cart-go-shopping">Go to shop</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gray-50" data-testid="cart-page">
      <div className="container mx-auto px-4 max-w-5xl">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">Your cart <span className="text-gray-400 text-2xl">({count})</span></h1>
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-4">
            {items.map(item => (
              <div key={item.id} className="flex gap-4 bg-white rounded-2xl p-4 border border-gray-100" data-testid={`cart-item-${item.slug}`}>
                <Link to={`/shop/${item.slug}`} className="w-24 h-24 rounded-xl overflow-hidden bg-pink-50 shrink-0">
                  <img src={imgSrc(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-3">
                    <Link to={`/shop/${item.slug}`} className="font-semibold text-gray-900 hover:text-pink-600 leading-snug">{item.name}</Link>
                    <button onClick={() => removeItem(item.id)} aria-label="Remove" data-testid={`cart-remove-${item.slug}`} className="text-gray-400 hover:text-red-600 shrink-0"><Trash2 className="w-5 h-5" /></button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{gbp(item.price)} each</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="inline-flex items-center border border-gray-200 rounded-full">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-2 hover:text-pink-600" aria-label="Decrease" data-testid={`cart-decrease-${item.slug}`}><Minus className="w-4 h-4" /></button>
                      <span className="w-8 text-center text-sm font-semibold" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-2 hover:text-pink-600" aria-label="Increase" data-testid={`cart-increase-${item.slug}`}><Plus className="w-4 h-4" /></button>
                    </div>
                    <span className="font-bold text-gray-900" data-testid={`cart-line-total-${item.slug}`}>{gbp(item.price * item.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-gray-100 lg:sticky lg:top-28" data-testid="cart-summary">
            <h2 className="font-bold text-lg text-gray-900 mb-4">Order summary</h2>
            <div className="flex justify-between text-gray-700 mb-2"><span>Subtotal</span><span data-testid="cart-subtotal">{gbp(subtotal)}</span></div>
            <div className="flex justify-between text-gray-700 mb-4"><span>Delivery</span><span className="text-green-700 font-medium">Free</span></div>
            <div className="flex justify-between text-gray-900 font-bold text-lg border-t pt-4 mb-6"><span>Total</span><span data-testid="cart-total">{gbp(subtotal)}</span></div>
            <Button onClick={() => navigate('/checkout')} size="lg" data-testid="cart-checkout-button" className="w-full bg-pink-500 hover:bg-pink-600 text-white rounded-full">
              Checkout <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Link to="/shop" className="block text-center text-sm text-gray-600 hover:text-pink-600 mt-4" data-testid="cart-continue-shopping">Continue shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
